import * as React from "react";
import {Component } from "react";

import {observer} from "mobx-react";
import _ from "lodash";
import {Button, Item, Message, Placeholder, Popup, Table} from "semantic-ui-react";
import {Auftragsverwaltung} from "../../stores/auftragsverwaltung-mobx";
import {Project} from "../../models/project";
import {PartialOrder} from "../../models/partial-order";
import {Icon} from "../../core/components/icon/icon";
import {PartialOrderRiskForm} from "./partial-order-risk-form";
import {AppState} from "../../components/app/app-mobx";

export interface PartialOrderRiskListProps {
    appStore: AppState;
    auftragsverwaltung: Auftragsverwaltung;
    project: Project;
    title?: string;
}
interface SortAction {
    type: string;
    column: string;
}
@observer
export class PartialOrderRiskList extends Component<PartialOrderRiskListProps, any> {
    watchHandles: any[] = [];
    constructor(props: PartialOrderRiskListProps) {
        super(props);
        this.state = {
            isLoading: true,
            error: null,
            column: null,
            data: null,
            direction: null,
            riskEvaluationCodes: null,
            primaryProcessCodes: null,
            sideProcessTypeCodes: null,
            recurrencePeriodCodes: null
        };
        this.sort = this.sort.bind(this);
        this.handleNewPartialOrder = this.handleNewPartialOrder.bind(this);
        this.handleCancel = this.handleCancel.bind(this);
        this.handleSave = this.handleSave.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
    }
    sort(action: SortAction): void {
        switch (action.type) {
        case "CHANGE_SORT":
            if (this.state.column === action.column) {
                this.setState((state: any) =>({
                    data: state.data.reverse(),
                    direction: state.direction === "ascending" ? "descending" : "ascending"
                }));
            } else {
                this.setState((state: any) =>({
                    column: action.column,
                    data: _.sortBy(state.data, [action.column], ["asc"]),
                    direction: "ascending",
                }));
            }
            return;
        default:
            throw new Error();
        }
    }

    componentDidMount(): void {
        this.loadDomains();
        this.loadPartialOrders();
    }

    componentDidUpdate(): void {}
    componentWillUnmount(): void {
        this.watchHandles.forEach((handle: any) => {
            handle.remove();
        });
        this.watchHandles.length = 0;
    }

    render(): React.ReactElement | null {
        const {appStore, auftragsverwaltung, project} = this.props;
        const {
            column, data, direction, isLoading, error,
            riskEvaluationCodes, primaryProcessCodes, sideProcessTypeCodes, recurrencePeriodCodes
        } = this.state;

        if (isLoading) {
            return (
                <div>
                    <h3 id="react-arcgis-loading-text">Teilaufträge laden...</h3>
                    <Placeholder>
                        <Placeholder.Header>
                            <Placeholder.Line/>
                        </Placeholder.Header>
                    </Placeholder>
                </div>
            );
        }

        if (error) {
            return (
                <Message negative>
                    <Message.Header>Fehler beim Laden der Teilaufträge</Message.Header>
                    {error}
                </Message>
            );
        }

        let newPartialOrderRiskButton: React.ReactElement | null = null;
        if (project.isAlterable) {
            newPartialOrderRiskButton = (
                <Popup content="Teilauftrag Risikoübersicht hinzufügen"
                    trigger={
                        <Button className={"new-partial-order"} circular
                            onClick={(e: React.MouseEvent) => {
                                e.preventDefault();
                                const target = e.target as HTMLElement;
                                target.blur();

                                this.handleNewPartialOrder(project);
                            }}>
                            <Icon name={"navigate_plus"} />
                        </Button>
                    } />
            );
        }

        let form: React.ReactElement | null = null;
        if (auftragsverwaltung.selectedPartialOrderRisk &&
            auftragsverwaltung.isEditingPartialOrderRisk) {
            form = (
                <PartialOrderRiskForm
                    appStore={appStore}
                    auftragsverwaltung={auftragsverwaltung}
                    project={project}
                    onCancel={this.handleCancel}
                    onSave={() => this.handleSave() }
                />
            );
        }

        if (data.length === 0) {
            return (
                <>
                    {newPartialOrderRiskButton}
                    {form}
                    <Item.Group className={"geo-partial-orders"}>
                        <Item><Item.Content><p>keine Teilaufträge Risikoübersicht</p></Item.Content></Item>
                    </Item.Group>
                </>
            );
        }

        return (
            <>
                {newPartialOrderRiskButton}
                {form}
                <Item.Group className={"geo-partial-orders"}>
                    <Item>
                        <Item.Content>
                            <Table basic="very" fixed singleLine sortable={(data.length > 1)} compact className={"partial-orders"}>
                                <Table.Header>
                                    <Table.Row>
                                        <Table.HeaderCell sorted={column === "riskEvaluationId" ? direction : null}
                                            onClick={() => this.sort({ type: "CHANGE_SORT", column: "riskEvaluationId" })}>
                                            Umfang Beurteilung
                                        </Table.HeaderCell>
                                        <Table.HeaderCell sorted={column === "primaryProcessTypeId" ? direction : null}
                                            onClick={() => this.sort({ type: "CHANGE_SORT", column: "primaryProcessTypeId" })}>
                                            Hauptprozessart
                                        </Table.HeaderCell>
                                        <Table.HeaderCell sorted={column === "sideProcessTypeId" ? direction : null}
                                            onClick={() => this.sort({ type: "CHANGE_SORT", column: "sideProcessTypeId" })}>
                                            Nebenprozessart
                                        </Table.HeaderCell>
                                        <Table.HeaderCell sorted={column === "processSourceName" ? direction : null}
                                            onClick={() => this.sort({ type: "CHANGE_SORT", column: "processSourceName" })}>
                                            Prozessquelle
                                        </Table.HeaderCell>
                                        <Table.HeaderCell sorted={column === "recurrencePeriodId" ? direction : null}
                                            onClick={() => this.sort({ type: "CHANGE_SORT", column: "recurrencePeriodId" })}>
                                            Wiederkehrperiode
                                        </Table.HeaderCell>
                                        {project.isAlterable &&
                                            <Table.HeaderCell></Table.HeaderCell>
                                        }
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {data.map((partialOrder: PartialOrder) => (
                                        <Table.Row key={partialOrder.objectId} >
                                            <Table.Cell>
                                                {riskEvaluationCodes.get(partialOrder.riskEvaluationId)}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {primaryProcessCodes.get(partialOrder.primaryProcessTypeId)}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {sideProcessTypeCodes.get(partialOrder.sideProcessTypeId)}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {partialOrder.processSourceName}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {recurrencePeriodCodes.get(partialOrder.recurrencePeriodId)}
                                            </Table.Cell>
                                            {project.isAlterable &&
                                                <Table.Cell>
                                                    <>
                                                        <Popup content="Teilauftrag Risikoübersicht hinzufügen"
                                                            trigger={
                                                                <Button className={"edit-partial-order"} circular
                                                                    onClick={(e: React.MouseEvent) => {
                                                                        e.preventDefault();
                                                                        const target = e.target as HTMLElement;
                                                                        target.blur();

                                                                        this.handleEdit(partialOrder);
                                                                    }}>
                                                                    <Icon name={"pencil"} />
                                                                </Button>
                                                            } />
                                                        <Popup content="Teilauftrag Risikoübersicht löschen"
                                                            trigger={
                                                                <Button className={"edit-partial-order"} circular
                                                                    onClick={(e: React.MouseEvent) => {
                                                                        e.preventDefault();
                                                                        const target = e.target as HTMLElement;
                                                                        target.blur();

                                                                        this.handleDelete(partialOrder);
                                                                    }}>
                                                                    <Icon name={"garbage_can"} />
                                                                </Button>
                                                            } />
                                                    </>
                                                </Table.Cell>
                                            }
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        </Item.Content>
                    </Item>
                </Item.Group>
            </>
        );
    }

    private handleNewPartialOrder(project: Project): void {
        const {auftragsverwaltung, appStore} = this.props;

        if (!project.id) {
            return;
        }

        const user = appStore.esriCredential?.userId || "";
        auftragsverwaltung.PartialOrderRiskStore.newItem(project.id, "partial-order-risk", user).then(() => {
            // do not show map when editing
            auftragsverwaltung.setShowMap(false);
            auftragsverwaltung.setEditing(true, "partial-order-risk");
        });
    }

    private handleCancel(): void {
        const {auftragsverwaltung} = this.props;
        auftragsverwaltung.setEditing(false);
    }

    private handleEdit(partialOrder: PartialOrder): void {
        const {auftragsverwaltung} = this.props;

        // do not show map when editing
        auftragsverwaltung.setShowMap(false);
        auftragsverwaltung.PartialOrderRiskStore.setSelectedItem(partialOrder);
        auftragsverwaltung.setEditing(true, "partial-order-risk");
    }

    private handleSave(): void {
        this.loadPartialOrders();
    }

    private handleDelete(partialOrder: PartialOrder): void {
        this.setState({
            isLoading: true
        });

        if (confirm("Teilprojekt wirklich löschen?")) {
            const {appStore, auftragsverwaltung, project} = this.props;

            const user = appStore.esriCredential?.userId || "";
            auftragsverwaltung.PartialOrderRiskStore.delete(project, partialOrder, user).then(() => {
                this.setState((state) => {
                    const data = state.data.filter((po) => po !== partialOrder);
                    return {
                        data,
                        isLoading: false
                    };
                });
            });
        }
    }

    private loadPartialOrders() {
        const {project, auftragsverwaltung} = this.props;
        //const {riskEvaluationCodes} = this.props;

        if (!project || !project.id) {
            return;
        }

        auftragsverwaltung.PartialOrderRiskStore.loadItems(project.id).then((partialOrders: PartialOrder[]) => {
            const data = _.orderBy(partialOrders, ["riskEvaluationId"], ["asc"]);
            this.setState({
                data,
                column: "riskEvaluationId",
                direction: "ascending",
                isLoading: false
            });
        }).catch((error) => {
            this.setState({
                error
            });
        });
    }

    /**
     * Load domains
     *
     * @private
     */
    private loadDomains() {
        const {auftragsverwaltung} = this.props;

        const domainPromises = [
            auftragsverwaltung.getShortDomainCodes("partial-order-risk", "riskEvaluationId"),
            auftragsverwaltung.getShortDomainCodes("partial-order-risk", "primaryProcessTypeId"),
            auftragsverwaltung.getShortDomainCodes("partial-order-risk", "sideProcessTypeId"),
            auftragsverwaltung.getStringDomainCodes("partial-order-risk", "recurrencePeriodId")
        ];

        Promise.all(domainPromises).then((domains) => {
            this.setState({
                riskEvaluationCodes: domains[0],
                primaryProcessCodes: domains[1],
                sideProcessTypeCodes: domains[2],
                recurrencePeriodCodes: domains[3]
            });
        });
    }

}
